import React, { useEffect, useState } from "react";
import iconEdit from "../../../../assets/images/iconEdit.png";
import DashboardHeader from "@/components/DashboardHeader";
import SelectSearchInput from "@/components/selectInput/SelectSearchInput";
import { SubmitHandler, useForm } from "react-hook-form";
import { FormHookInput, Button, emailValidator, namePattern, phoneNumberPattern, NON_DIGIT_REGEX, STARTS_WITH_OPTIONAL_1_REGEX } from "@thdinc/hpf-shared";
import userDefaultImg from "../../../../assets/images/defaultuser.jpg";
import { useLocation, useNavigate } from "react-router";
import { GET } from "@/services/api";
import { API_ROUTES } from "@/services/environment";
import { useAppSelector } from "@/hooks/hook";
import {
    ACCEPTED_IMAGE_TYPES,
    GENDER_DATA,
    MAX_FILE_SIZE_MB,
} from "@/constants/Common";
import useLoading from "@/hooks/useLoading";
import { Input, Loader} from "@thdinc/hpf-shared";
import useError from "@/hooks/useError ";
import FormPasswordInput from "@/components/formHookInput/formPasswordInput";
import { UserBartender } from "../../interface";

interface addBartender {
    isAdmin?: boolean;
    businessId?: string;
    isView?: boolean;
}

type Inputs = {
    name: string;
    password: string;
    lastName: string;
    email: string;
    phone_number: string;
    fee: number;
};
const BartenderDetail: React.FC<addBartender> = ({ isAdmin, businessId, isView }) => {
    const { bartenders, businessAdmin } = API_ROUTES;
    const { refreshToken } = useAppSelector((store) => store.user);
    const { isLoading, startLoading, stopLoading } = useLoading();
    const [error, setCustomError] = useError()

    const location = useLocation();
    const receivedData = location.state?.data;

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        formState: { errors, isSubmitSuccessful },
    } = useForm<Inputs>();
    const [imageUrl, setImageUrl] = useState<File | null>(null);
    const [profile, setProfile] = useState<File | null>(null);
    const [isBartender, setIsBartender] = useState<boolean>(true);
    const [errorState, setErrorState] = useState<{
        name: string | null;
        error: string | null;
    }>({ name: "", error: "" });
    const [selectedGender, setSelectedGender] = useState<string>(
        receivedData?.gender ?? "Male"
    );
    const [selectLang, setSelectLang] = useState<string[]>(
        receivedData?.languages ?? []
    );


    const navigate = useNavigate();
    // const removeImage = () => {
    //   setImageUrl(null);
    // };

    const [modal, setModal] = useState<boolean>(false);
    const [activeData, setActiveData] = useState<any | null>(null);
    const [adminData, setAdminData] = useState<UserBartender | null>(null)

    useEffect(() => {
        if (isView) {
            getData()
        }
    }, [])

    const getData = async () => {
        // get the details of the admin data
        startLoading()

        try {
            const result = await GET(`${businessAdmin}/${businessId}`, refreshToken);
            const data = result.data.data
            if (result.data.success === true) {
                setAdminData(result?.data.data)
                setValue("name", data.first_name)
                setValue("lastName", data.last_name)
                setValue("email", data.email)
                setValue("phone_number", data.phone_number)
                setSelectLang(data.languages)
            }
        } catch (err) {
            console.log(String(err));
        } finally {
            stopLoading();
        }
    }

    const selectGenderFun = (gender: string) => {
        setSelectedGender(gender);
        if (errorState.name === "gender") {
            setErrorState({ name: null, error: null });
        }
    };

    const selectLangFun = (checked: boolean, lang: string) => {
        if (checked) {
            setSelectLang([...selectLang, lang]);
            setErrorState({ name: null, error: null });
        } else {
            setSelectLang(selectLang.filter((item) => item !== lang));
        }
    };

    const onSubmit: SubmitHandler<Inputs> = async (data) => {
        try {
            if (
                (profile && selectedGender !== "null" && selectLang.length !== 0)
            ) {
                startLoading();
                const formData = new FormData();
                formData.append("first_name", data.name);
                formData.append("last_name", data.lastName);
                formData.append("gender", selectedGender);
                formData.append("email", data.email);
                formData.append("phone_number", data.phone_number);
                formData.append("password", data.password);

                formData.append("language", JSON.stringify(selectLang));
                if (isAdmin && businessId) {
                    formData.append("is_admin", isAdmin.toString())
                    formData.append("business_id", businessId)
                    formData.append("is_bartender", isBartender.toString())
                }
                // if profile image selected
                if (profile) {
                    formData.append("avatar", profile);
                }
                if (imageUrl) {
                    formData.append("other_photos", imageUrl);
                }
                setActiveData(formData);
                setModal(true);
            }

            //if user wants to edit admin profile
            if (!profile && !isView) {
                setErrorState({ name: "dp", error: "Please upload profile image" });
            } else if (selectedGender === "null") {
                setErrorState({ name: "gender", error: "Please select a gender" });
            } else if (selectLang.length === 0) {
                setErrorState({ name: "lang", error: "Please select a language" });
            }
        } catch (error) {
            console.log(error);
        } finally {
            stopLoading();
        }
    };
    // const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
    const handleProfileImg = (e: React.ChangeEvent<HTMLInputElement>) => {
        // profile image selection for admin
        const file = e.target.files?.[0];
        if (file) {
            if (ACCEPTED_IMAGE_TYPES.includes(file.type)) {
                if (file.size <= MAX_FILE_SIZE_MB * 1024 * 1024) {
                    // Convert MB to bytes
                    const fileWithTimestamp = addTimestampToFile(file);
                    setProfile(fileWithTimestamp); // Set profile image with timestamped file name
                    setErrorState({ name: null, error: null });
                } else {
                    setErrorState({
                        name: "dp",
                        error: `File size exceeds ${MAX_FILE_SIZE_MB} MB. Please choose a smaller image.`,
                    });
                }
            } else {
                //if wrong type of files selected
                setErrorState({
                    name: "dp",
                    error:
                        "Please select a valid image file (JPEG, PNG, GIF, BMP, or TIFF).",
                });
            }
        }
    };

    // in order to add time stamp in image file data
    function addTimestampToFile(file: File): File {
        const timestamp = Date.now(); // Get current timestamp
        const fileName = `${timestamp}_${file.name}`; // Add timestamp to file name
        return new File([file], fileName, { type: file.type }); // Create a new file object with timestamped name
    }



    if (isView && !adminData) {
        return null
    }

    return (
        <div>
            <DashboardHeader title={isView ? "Admin Details" : "Add Admin"} />
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="container mx-auto  md:gap-4"
            >
                {/* left div */}
                <div className="bg-white rounded-2xl border p-6 w-full shadow-md flex md:flex-row flex-col items-center gap-5 ">
                    <div className="flex flex-col  items-center  ">
                        <div className="justify-center relative flex ">
                            <img
                                className="mt-4 w-56 h-56 object-cover rounded-full  border-primary-red border-2 self-center"
                                src={
                                    isView ? adminData?.avatar :
                                        profile
                                            ? URL.createObjectURL(profile)
                                            : receivedData?.avatar
                                                ? receivedData?.avatar
                                                : userDefaultImg
                                }
                            />
                            {/* <div className=" font-semibold my-2">*</div> */}
                            {
                                !isView &&
                                <div className="bg-primary-red absolute rounded-full p-2 bottom-0 ml-24  cursor-pointer">
                                    <label className=" cursor-pointer">
                                        <img className="h-3 w-auto" src={iconEdit} />
                                        <input
                                            accept="image/*"
                                            id="profile-img"
                                            onChange={handleProfileImg}
                                            type="file"
                                            className="hidden"
                                        />
                                    </label>
                                </div>
                            }
                        </div>
                        <div className="text-center">
                            {errorState.name === "dp" && (
                                <p className="text-red-500 text-xs mt-1 py-">
                                    {errorState.error}
                                </p>
                            )}
                            <p className="text-gray-500 text-xs mt-2 md:w-8/12 mx-auto ">
                                Please upload an image in one of the accepted formats: JPEG, PNG
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col items-center justify-center mt-4 w-full">
                        <div className="flex gap-4 flex-col md:flex-row w-full  ">
                            <div className="w-full ">
                                <div className=" font-semibold my-2">First Name </div>
                                <FormHookInput
                                    type="text"
                                    disabled={isView}
                                    placeholder="First Name"
                                    register={register("name", {
                                        required: "First Name is required",
                                        maxLength: {
                                            value: 50,
                                            message:
                                                "First Name length can't be more than 50 characters.",
                                        },
                                        pattern: {
                                            value: namePattern, // allow letters, hyphens, and apostrophes, between 1 and 50 characters
                                            message: "Invalid first name format",
                                        },
                                    })}
                                    error={errors.name}
                                />
                            </div>
                            <div className="w-full ">
                                <div className=" font-semibold my-2">Last Name </div>
                                <FormHookInput
                                    type="text"
                                    disabled={isView}
                                    placeholder="Last Name"
                                    register={register("lastName", {
                                        required: "Last First Name is required",
                                        maxLength: {
                                            value: 50,
                                            message:
                                                "Last Name length can't be more than 50 characters.",
                                        },

                                        pattern: {
                                            value: namePattern, // allow letters, hyphens, and apostrophes, between 1 and 50 characters
                                            message: "Invalid Last Name format",
                                        },
                                    })}
                                    error={errors.lastName}
                                />
                            </div>
                        </div>
                        <div className="flex gap-4 w-full flex-col md:flex-row">
                            <div className="w-full ">
                                <div className=" font-semibold my-2">Gender </div>
                                <SelectSearchInput
                                    disabled={isView ? true : false}
                                    options={GENDER_DATA}
                                    value={isView ? adminData?.gender : selectedGender}
                                    onChange={selectGenderFun}
                                />
                                {errorState.name === "gender" && (
                                    <p className="text-red-500 text-xs mt-1 py-">
                                        {errorState.error}
                                    </p>
                                )}
                            </div>
                            <div className="w-full ">
                                <div className=" font-semibold my-2">Phone</div>
                                <FormHookInput
                                    disabled
                                    type="text"
                                    placeholder="Phone Number"
                                    register={register("phone_number", {
                                        required: "Phone number is required",
                                        pattern: {
                                            value: phoneNumberPattern,
                                            message: "Please enter a valid U.S format.",
                                        },
                                        onChange: (e) => {
                                            let { value } = e.target;
                                            // Remove all non-digit characters except the initial "+1 "
                                            value = value
                                                .replace(NON_DIGIT_REGEX, "")
                                                .replace(STARTS_WITH_OPTIONAL_1_REGEX, "1"); // Ensures it starts with "1"
                                            value = `+1 ${value.substring(1, 4)} ${value.substring(
                                                4,
                                                7
                                            )} ${value.substring(7, 11)}`.trim();
                                            e.target.value = value;
                                        },
                                    })}
                                    error={errors.phone_number}
                                />
                            </div>
                        </div>
                        <div className="flex gap-4 w-full flex-col md:flex-row">
                            <div className="w-full ">
                                <div className=" font-semibold my-2">Email </div>
                                <FormHookInput
                                    type="email"
                                    disabled={isView}
                                    placeholder="Email"
                                    register={register("email", {
                                        required: "Email is required!",
                                        pattern: emailValidator,
                                    })}
                                    error={errors.email}
                                />
                            </div>
                            {
                                !isView &&
                                <div className="w-full ">
                                    <div className=" font-semibold my-2">Password </div>
                                    <FormPasswordInput
                                        type="password"
                                        placeholder="Password"
                                        register={register("password", {
                                            required: "Password is required",
                                        })}
                                        error={errors.password}
                                    />
                                </div>
                            }
                        </div>

                        <div className="flex gap-4 w-full flex-col md:flex-row">


                            <div className="w-full ">
                                <div className=" font-semibold my-2">Language </div>

                                <div className="flex justify-start mt-2 gap-5">
                                    <div className={`flex items-center gap-2 ${selectLang.includes("en") && "bg-[#ffeee5] "
                                        } px-3 py-2 rounded-full`}>
                                        <p>English</p>
                                        <Input
                                            disabled={isView}
                                            onChange={(e) => selectLangFun(e.target.checked, "en")}
                                            className="cursor-pointer appearance-none h-5 w-5 border border-gray-300 rounded-full checked:bg-primary-red checked:border-transparent focus:outline-none"
                                            type="checkbox"
                                            checked={selectLang.includes("en")}
                                        />
                                    </div>
                                    <div className={`flex items-center gap-2  ${selectLang.includes("es") && "bg-[#ffeee5] "
                                        } px-3 py-2 rounded-full`}>
                                        <p>Spanish</p>
                                        <Input
                                            disabled={isView}
                                            onChange={(e) => selectLangFun(e.target.checked, "es")}
                                            checked={selectLang.includes("es")}
                                            className="cursor-pointer appearance-none h-5 w-5 border border-gray-300 rounded-full checked:bg-primary-red checked:border-transparent focus:outline-none"
                                            type="checkbox"
                                        />
                                    </div>
                                </div>
                                {errorState.name === "lang" && (
                                    <p className="text-red-500 text-xs mt-1 py-">
                                        {errorState.error}
                                    </p>
                                )}
                            </div>
                            {/* {
                                !isView &&

                                <div className="flex items-center w-full gap-2">
                                    <p>Is Bartender</p>
                                    <Input
                                        onChange={(e) => setIsBartender(e.target.checked)}
                                        checked={isBartender}
                                        className="w-4 h-4 cursor-pointer accent-primary-red"
                                        type="checkbox"
                                    />
                                </div>
                            } */}

                        </div>
                        {
                            !isView &&
                            <div className="flex justify-end mt-5 w-full gap-4">
                                <Button type="submit" disabled={isLoading}>
                                    {" "}
                                    {isLoading ? <Loader  color="bg-gray-50" /> : "Next"}
                                </Button>
                            </div>
                        }


                        {error?.name === 'add' && <p className="text-primary-red text-center mt-">{error.message}</p>}

                    </div>
                </div>

            </form>

        </div>
    );
};

export default BartenderDetail;
